import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDeviceModel, useDeviceVariants } from '@/hooks/useCatalog';
import { useDeleteDeviceVariant } from '@/hooks/useCatalogActions';
import type { DeviceVariant } from '@octave/contracts';
import { cn } from '@/lib/utils';
import {
  ChevronLeft,
  Package,
  Pencil,
  Plus,
  Settings,
  Smartphone,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DeviceModelForm } from './DeviceModelForm';
import { DeviceVariantForm } from './DeviceVariantForm';
import { formatPrice } from '@/lib/format';

export function DeviceModelDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isModelFormOpen, setIsModelFormOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState<DeviceVariant | null>(
    null
  );
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    variant: DeviceVariant | null;
  }>({ open: false, variant: null });

  const { data: deviceModel, isLoading: isLoadingModel } = useDeviceModel(id);
  const { data: variants, isLoading: isLoadingVariants } = useDeviceVariants(id);
  const { mutate: deleteDeviceVariant, isPending: isDeletingVariant } =
    useDeleteDeviceVariant();

  const handleEdit = (variant: DeviceVariant) => {
    setEditingVariant(variant);
    setIsFormOpen(true);
  };

  const handleDeleteVariant = () => {
    if (!deleteDialog.variant) return;
    deleteDeviceVariant(deleteDialog.variant.id, {
      onSuccess: () => setDeleteDialog({ open: false, variant: null }),
    });
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingVariant(null);
  };

  
  const formatCapacity = (capacity: number) =>
    capacity >= 1024 ? `${capacity / 1024} To` : `${capacity} Go`;

  // Loading skeleton
  if (isLoadingModel || !deviceModel) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-32 rounded-xl" />
        <div className="rounded-2xl bg-white p-8 shadow-sm space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-56" />
            <Skeleton className="h-7 w-20 rounded-full" />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16 rounded-xl" />
            ))}
          </div>
        </div>
        <Skeleton className="h-6 w-40" />
        <div className="rounded-2xl bg-white p-6 shadow-sm">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate('/o/admin/catalog')}
        className="group flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-400 transition-all hover:bg-white hover:text-slate-600 hover:shadow-sm"
      >
        <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
        Retour au catalogue
      </button>

      {/* Hero card */}
      <div className="rounded-2xl bg-white p-4 sm:p-8 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 overflow-hidden">
              {deviceModel.imageUrl ? (
                <img
                  src={deviceModel.imageUrl}
                  alt={deviceModel.name}
                  className="h-10 w-10 object-contain"
                />
              ) : (
                <Smartphone className="h-6 w-6 text-slate-400" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {deviceModel.brand} {deviceModel.name}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700">
                  {deviceModel.category}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
                    deviceModel.isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-slate-100 text-slate-500'
                  )}
                >
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      deviceModel.isActive ? 'bg-emerald-500' : 'bg-slate-400'
                    )}
                  />
                  {deviceModel.isActive ? 'Actif' : 'Inactif'}
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsModelFormOpen(true)}
            className="bg-white border-slate-200 text-slate-600 gap-1.5"
          >
            <Settings className="h-4 w-4" />
            Modifier
          </Button>
        </div>

        {/* Info strip */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl bg-slate-50 px-4 py-3">
            <p className="text-xs text-slate-400">Marque</p>
            <p className="mt-0.5 text-sm font-semibold text-slate-900">
              {deviceModel.brand}
            </p>
          </div>
          <div className="rounded-xl bg-slate-50 px-4 py-3">
            <p className="text-xs text-slate-400">Catégorie</p>
            <p className="mt-0.5 text-sm font-semibold text-slate-900">
              {deviceModel.category}
            </p>
          </div>
          <div className="rounded-xl bg-slate-50 px-4 py-3">
            <p className="text-xs text-slate-400">Déclinaisons</p>
            <p className="mt-0.5 text-sm font-semibold text-slate-900">
              {variants?.length || 0}
            </p>
          </div>
          <div className="rounded-xl bg-slate-50 px-4 py-3">
            <p className="text-xs text-slate-400">Image</p>
            {deviceModel.imageUrl ? (
              <p className="mt-0.5 text-sm font-semibold text-emerald-600">
                Configurée
              </p>
            ) : (
              <p className="mt-0.5 text-sm text-slate-400">Aucune</p>
            )}
          </div>
        </div>
      </div>

      {/* Edit model dialog */}
      <Dialog open={isModelFormOpen} onOpenChange={setIsModelFormOpen}>
        <DialogContent className="!max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">
              Modifier le modèle
            </DialogTitle>
          </DialogHeader>
          <DeviceModelForm
            model={deviceModel}
            onSuccess={() => setIsModelFormOpen(false)}
            onCancel={() => setIsModelFormOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Variants section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-slate-900">Déclinaisons</h2>
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-slate-100 text-slate-600">
              {variants?.length || 0}
            </span>
          </div>
          <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditingVariant(null)}
                className="bg-white border-slate-200 text-slate-600 gap-1.5"
              >
                <Plus className="h-4 w-4" />
                Nouvelle déclinaison
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-slate-900">
                  {editingVariant
                    ? editingVariant.id
                      ? 'Modifier la déclinaison'
                      : 'Dupliquer la déclinaison'
                    : 'Nouvelle déclinaison'}
                </DialogTitle>
              </DialogHeader>
              <DeviceVariantForm
                deviceModelId={deviceModel.id}
                variant={editingVariant}
                onSuccess={handleFormClose}
                onCancel={handleFormClose}
              />
            </DialogContent>
          </Dialog>
        </div>

        <div className="rounded-2xl bg-white shadow-sm overflow-hidden">
          {isLoadingVariants ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : variants && variants.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead className="text-xs font-medium text-slate-400">
                      Nom
                    </TableHead>
                    <TableHead className="text-xs font-medium text-slate-400">
                      Condition
                    </TableHead>
                    <TableHead className="text-xs font-medium text-slate-400">
                      Prix
                    </TableHead>
                    <TableHead className="text-xs font-medium text-slate-400">
                      Poids
                    </TableHead>
                    <TableHead className="text-xs font-medium text-slate-400">
                      Couleur
                    </TableHead>
                    <TableHead className="text-xs font-medium text-slate-400">
                      Capacité
                    </TableHead>
                    <TableHead className="text-xs font-medium text-slate-400">
                      Statut
                    </TableHead>
                    <TableHead className="text-xs font-medium text-slate-400 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {variants.map((variant) => (
                    <TableRow
                      key={variant.id}
                      className="border-slate-100 hover:bg-slate-50/50 transition-colors"
                    >
                      <TableCell className="text-sm font-medium text-slate-900">
                        {variant.name}
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
                            variant.condition === 'NEW'
                              ? 'bg-sky-50 text-sky-700'
                              : 'bg-amber-50 text-amber-700'
                          )}
                        >
                          {variant.condition === 'NEW'
                            ? 'Neuf'
                            : 'Reconditionné'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm font-semibold text-slate-900 tabular-nums">
                        {formatPrice(variant.price)}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {variant.weight}g
                      </TableCell>
                      <TableCell>
                        {variant.color ? (
                          <span className="text-sm text-slate-700">
                            {variant.color}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-300">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {variant.capacity ? (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-600">
                            {formatCapacity(variant.capacity)}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-300">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                            variant.isActive
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-slate-100 text-slate-500'
                          )}
                        >
                          <span
                            className={cn(
                              'h-1.5 w-1.5 rounded-full',
                              variant.isActive
                                ? 'bg-emerald-500'
                                : 'bg-slate-400'
                            )}
                          />
                          {variant.isActive ? 'Actif' : 'Inactif'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleEdit(variant)}
                                  className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Modifier</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() =>
                                    setDeleteDialog({ open: true, variant })
                                  }
                                  className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Supprimer</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Package className="h-10 w-10 mb-3 text-slate-300" />
              <p className="text-sm font-medium">Aucune déclinaison</p>
              <p className="text-xs mt-1">
                Créez-en une pour commencer à vendre ce produit
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Delete variant dialog */}
      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, variant: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la déclinaison</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer{' '}
              <strong>{deleteDialog.variant?.name}</strong> ? Cette action est
              irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, variant: null })}
              disabled={isDeletingVariant}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteVariant}
              disabled={isDeletingVariant}
            >
              {isDeletingVariant ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
